import type { Rng } from './rng';
import { createRng, defaultRng } from './rng';

/**
 * Admin-portal-specific fixtures: validation rules, system metrics,
 * TickStar integration health, PKI certificates, accreditation reports.
 *
 * All seeded so screenshots stay stable between reloads.
 */

// --- Validation rules (M3) ---------------------------------------

export type ValidationRuleScope = 'syntax' | 'business' | 'trn' | 'schematron';
export type ValidationRuleSeverity = 'error' | 'warning' | 'info';
export type ValidationRuleStatus = 'active' | 'disabled' | 'draft';

export interface ValidationRule {
  ruleId: string;
  code: string;
  scope: ValidationRuleScope;
  severity: ValidationRuleSeverity;
  status: ValidationRuleStatus;
  /** Plain-language summary in English. */
  description: string;
  /** Affected field(s) — comma separated. */
  field: string;
  /** "v3" — increments on each edit. */
  version: number;
  /** Last-modified UTC timestamp. */
  updatedAt: string;
  updatedBy: string;
  /** Source: built-in IBR-AE rule, lokal solara business rule, or custom tenant override. */
  source: 'IBR-AE' | 'Solara' | 'Custom';
}

const BUILTIN_RULES: Omit<ValidationRule, 'ruleId' | 'updatedAt' | 'updatedBy' | 'version'>[] = [
  { code: 'E001',     scope: 'syntax',     severity: 'error',   status: 'active',  source: 'IBR-AE',  field: 'seller_trn / buyer_trn',          description: 'TRN must be exactly 15 numeric chars, starts with "1".' },
  { code: 'E002',     scope: 'syntax',     severity: 'error',   status: 'active',  source: 'IBR-AE',  field: 'issue_date',                       description: 'Issue date ISO 8601 (YYYY-MM-DD), not in the future.' },
  { code: 'E003',     scope: 'business',   severity: 'error',   status: 'active',  source: 'IBR-AE',  field: 'line_items[].vat_rate',           description: 'VAT rate must be 5%, 0% or Exempt for UAE invoices.' },
  { code: 'E004',     scope: 'business',   severity: 'error',   status: 'active',  source: 'Solara',  field: 'totals',                           description: 'Sum(line totals) = grand total (±0.01 AED).' },
  { code: 'E005',     scope: 'business',   severity: 'error',   status: 'active',  source: 'Solara',  field: 'invoice_number',                   description: 'Invoice number unique per seller (duplicate detection).' },
  { code: 'E006',     scope: 'business',   severity: 'error',   status: 'active',  source: 'IBR-AE',  field: 'currency',                         description: 'AED required for UAE B2B transactions (ISO 4217).' },
  { code: 'E007',     scope: 'syntax',     severity: 'error',   status: 'active',  source: 'IBR-AE',  field: 'line_items[].description',        description: 'Each line item must have a non-empty description (max 500 chars).' },
  { code: 'E008',     scope: 'syntax',     severity: 'error',   status: 'active',  source: 'Solara',  field: 'timestamp',                        description: 'UTC timestamp in ISO 8601 with Z suffix.' },
  { code: 'E010',     scope: 'syntax',     severity: 'error',   status: 'active',  source: 'Solara',  field: 'contact_email',                    description: 'Email must be RFC 5322 compliant.' },
  { code: 'E011',     scope: 'syntax',     severity: 'error',   status: 'active',  source: 'Solara',  field: 'contact_phone',                    description: 'Phone must be E.164 international format.' },
  { code: 'E020',     scope: 'syntax',     severity: 'error',   status: 'active',  source: 'IBR-AE',  field: 'currency',                         description: 'Currency code is ISO 4217 (3 uppercase letters).' },
  { code: 'E030',     scope: 'syntax',     severity: 'error',   status: 'active',  source: 'IBR-AE',  field: 'line_items[].quantity',           description: 'Quantity is positive, max 6 decimals.' },
  { code: 'E031',     scope: 'syntax',     severity: 'error',   status: 'active',  source: 'IBR-AE',  field: 'line_items[].unit_price',         description: 'Unit price ≥ 0.01, max 2 decimals.' },
  { code: 'E040',     scope: 'syntax',     severity: 'error',   status: 'active',  source: 'Solara',  field: 'idempotency_key',                  description: 'Idempotency-Key required, printable ASCII, ≤ 128 chars.' },
  { code: 'E050',     scope: 'syntax',     severity: 'error',   status: 'active',  source: 'IBR-AE',  field: 'peppol_participant_id',           description: 'Peppol participant ID format: 0088:AETRN[15-digit TRN].' },
  { code: 'BR-001',   scope: 'trn',        severity: 'error',   status: 'active',  source: 'IBR-AE',  field: 'buyer_trn',                        description: 'Buyer TRN must exist in the EmaraTax registry.' },
  { code: 'BR-002',   scope: 'business',   severity: 'error',   status: 'active',  source: 'Solara',  field: 'totals',                           description: 'Mathematical consistency: gross = net + VAT.' },
  { code: 'BR-003',   scope: 'business',   severity: 'warning', status: 'active',  source: 'Solara',  field: 'line_items[].vat_amount',         description: 'Per-line VAT consistent with qty × price × rate (±0.01).' },
  { code: 'BR-004',   scope: 'business',   severity: 'error',   status: 'active',  source: 'Solara',  field: 'delivery_date',                    description: 'Delivery date ≥ issue date.' },
  { code: 'BR-005',   scope: 'business',   severity: 'error',   status: 'active',  source: 'IBR-AE',  field: 'currency',                         description: 'UAE B2B → currency must be AED.' },
  { code: 'BR-006',   scope: 'business',   severity: 'error',   status: 'active',  source: 'Solara',  field: 'invoice_number',                   description: 'Invoice number unique per tenant.' },
  { code: 'BR-007',   scope: 'business',   severity: 'error',   status: 'active',  source: 'Solara',  field: 'tdd.deadline',                     description: 'TDD submission must occur within 14 days of issue.' },
  { code: 'BR-008',   scope: 'syntax',     severity: 'error',   status: 'active',  source: 'IBR-AE',  field: 'line_items',                       description: 'At least one line item required.' },
  { code: 'BR-009',   scope: 'business',   severity: 'error',   status: 'active',  source: 'Solara',  field: 'signing.certificate_expiry',      description: 'Signing certificate must be valid at signing time.' },
  { code: 'BR-010',   scope: 'business',   severity: 'error',   status: 'active',  source: 'Solara',  field: 'archive_id',                       description: 'Archived invoice immutable; require credit note for amendments.' },
  { code: 'BR-011',   scope: 'business',   severity: 'error',   status: 'active',  source: 'IBR-AE',  field: 'sender_participant_id',           description: 'Inbound C3: sender PKI must be valid in Peppol directory.' },
  { code: 'BR-012',   scope: 'business',   severity: 'error',   status: 'active',  source: 'Solara',  field: 'batch.size',                       description: 'Batch upload limited to 1000 invoices per request.' },
  { code: 'BR-AE-VAT-08', scope: 'schematron', severity: 'error', status: 'active', source: 'IBR-AE', field: 'tax_category_code',               description: 'VAT category must align with PINT-AE taxonomy.' },
  { code: 'BR-AE-CUST-04', scope: 'schematron', severity: 'warning', status: 'active', source: 'IBR-AE', field: 'customer_party',           description: 'Customer endpoint ID matches buyer_trn-derived participant.' },
  { code: 'AE-CUSTOM-001', scope: 'business', severity: 'info', status: 'draft',    source: 'Custom',  field: 'cost_center',                     description: 'Pilot rule: validate cost-center prefix on tenant override.' },
];

export function generateValidationRules(seed = 0xa14d_ae): ValidationRule[] {
  const rng = createRng(seed);
  return BUILTIN_RULES.map((r, idx) => ({
    ...r,
    ruleId: `rule-${String(idx + 1).padStart(3, '0')}`,
    version: rng.int(1, 8),
    updatedAt: new Date(Date.now() - rng.int(86_400_000, 200 * 86_400_000)).toISOString(),
    updatedBy: rng.pick([
      'platform-admin@solara.ae',
      'compliance@solara.ae',
      'auto-import-fta-2026',
    ]),
  }));
}

// --- System metrics (M7) -----------------------------------------

export interface SystemHealth {
  /** API p95 over last 5 min, milliseconds. */
  apiP95Ms: number;
  /** Validation queue depth (Kafka topic). */
  validationQueueDepth: number;
  /** Signing queue depth. */
  signingQueueDepth: number;
  /** Peppol AS4 queue depth. */
  peppolQueueDepth: number;
  /** Active websocket sessions. */
  socketSessions: number;
  /** Throughput last 24 h (per-hour invoice counts). */
  throughputSeries: number[];
  /** Rejection rate per hour, last 24 h. */
  rejectionRateSeries: number[];
  /** Per-module p95 ms. */
  modulep95: { module: string; p95Ms: number; status: 'healthy' | 'degraded' | 'failing' }[];
  /** Active alerts that page on-call. */
  activeAlerts: { id: string; severity: 'info' | 'warning' | 'critical'; title: string; firedAt: string }[];
}

export function generateSystemHealth(seed = 0x5_15): SystemHealth {
  const rng = createRng(seed);
  const throughputSeries = Array.from({ length: 24 }, () => rng.int(40, 260));
  const rejectionRateSeries = Array.from({ length: 24 }, () => Number((rng.next() * 0.06).toFixed(4)));
  return {
    apiP95Ms: rng.int(180, 480),
    validationQueueDepth: rng.int(0, 380),
    signingQueueDepth: rng.int(0, 60),
    peppolQueueDepth: rng.int(0, 120),
    socketSessions: rng.int(120, 1100),
    throughputSeries,
    rejectionRateSeries,
    modulep95: [
      { module: 'Intake API',          p95Ms: rng.int(120, 280),  status: 'healthy' },
      { module: 'Validation',          p95Ms: rng.int(380, 950),  status: rng.chance(0.85) ? 'healthy' : 'degraded' },
      { module: 'Signing (HSM)',       p95Ms: rng.int(160, 320),  status: 'healthy' },
      { module: 'Peppol AS4',          p95Ms: rng.int(800, 4500), status: rng.chance(0.85) ? 'healthy' : 'degraded' },
      { module: 'TDD (EmaraTax)',      p95Ms: rng.int(900, 2200), status: 'healthy' },
      { module: 'Archive (WORM)',      p95Ms: rng.int(450, 1300), status: 'healthy' },
      { module: 'Webhook receiver',    p95Ms: rng.int(40,  140),  status: 'healthy' },
    ],
    activeAlerts: [
      {
        id: 'alert-001',
        severity: 'warning',
        title: 'TDD submission p95 elevated (EmaraTax bridge)',
        firedAt: new Date(Date.now() - 28 * 60_000).toISOString(),
      },
      {
        id: 'alert-002',
        severity: 'info',
        title: 'Scheduled maintenance — Sunday 02:00–04:00 GST',
        firedAt: new Date(Date.now() - 6 * 3600_000).toISOString(),
      },
    ],
  };
}

// --- Integration event log (legacy: Qvalia, now TickStar) --------

export interface IntegrationEvent {
  eventId: string;
  at: string;
  endpoint: 'pint-ae-validate' | 'as4-send' | 'as4-receive' | 'tdd-submit' | 'archive-write' | 'analytics-pull';
  outcome: 'ok' | 'retry' | 'dlq' | 'fail';
  durationMs: number;
  invoiceId?: string;
  errorCode?: string;
  errorMessage?: string;
  retryCount: number;
}

export function generateIntegrationEvents(count = 30, seed = 0xa00_a_ad): IntegrationEvent[] {
  const rng = createRng(seed);
  const ENDPOINTS: IntegrationEvent['endpoint'][] = [
    'pint-ae-validate',
    'as4-send',
    'as4-receive',
    'tdd-submit',
    'archive-write',
    'analytics-pull',
  ];
  const out: IntegrationEvent[] = [];
  for (let i = 0; i < count; i++) {
    const endpoint = rng.pick(ENDPOINTS);
    const r = rng.next();
    const outcome: IntegrationEvent['outcome'] =
      r < 0.85 ? 'ok' : r < 0.93 ? 'retry' : r < 0.97 ? 'dlq' : 'fail';
    const at = new Date(Date.now() - rng.int(0, 24 * 3600_000));
    out.push({
      eventId: `int-${String(rng.int(1, 999_999_999)).padStart(9, '0')}`,
      at: at.toISOString(),
      endpoint,
      outcome,
      durationMs: rng.int(40, endpoint === 'tdd-submit' ? 4500 : 1800),
      invoiceId: rng.chance(0.85)
        ? `${'aaaaaaaa'}-${rng.int(1000, 9999).toString(16)}-4abc-aaaa-${'bbbbbbbbbbbb'}`
        : undefined,
      errorCode:
        outcome === 'ok'
          ? undefined
          : rng.pick(['BR-AE-VAT-08', 'AE-AB-13', 'EMTX-503', 'AS4-TIMEOUT', 'DNS-RESOLVE']),
      errorMessage:
        outcome === 'ok'
          ? undefined
          : rng.pick([
              'Schematron BR-AE-VAT-08: tax category mismatch',
              'EmaraTax 503: upstream timeout',
              'AS4 receiver returned EBMS:0202 — invalid signature',
              'DNS resolution failed for recipient endpoint',
            ]),
      retryCount: outcome === 'ok' ? 0 : rng.int(1, 3),
    });
  }
  out.sort((a, b) => (a.at < b.at ? 1 : -1));
  return out;
}

// --- Certificates / HSM ------------------------------------------

export interface SampleCertificate {
  certId: string;
  name: string;
  type: 'peppol-pki' | 'mtls-emaratax' | 'mtls-tickstar' | 'rfc-3161-tsa' | 'tls-cdn';
  /** SHA-1 thumbprint for display. */
  thumbprint: string;
  algorithm: 'RSA-2048' | 'RSA-4096' | 'ECDSA-P256' | 'ECDSA-P384';
  issuer: string;
  notBefore: string;
  notAfter: string;
  ocspStatus: 'good' | 'revoked' | 'unknown';
  storedIn: 'CloudHSM' | 'Secrets Manager' | 'KMS-managed';
}

export interface SampleHsm {
  clusterId: string;
  region: string;
  status: 'healthy' | 'degraded' | 'failing';
  primaryHsm: string;
  secondaryHsm: string;
  signingsLast24h: number;
  cpuPct: number;
}

export function generateCertificates(seed = 0xce_47): SampleCertificate[] {
  const rng = createRng(seed);
  const fakeThumbprint = () =>
    Array.from({ length: 40 }, () => rng.int(0, 15).toString(16).toUpperCase()).join('');
  const items: { name: string; type: SampleCertificate['type']; storedIn: SampleCertificate['storedIn']; daysToExpiry: number; alg: SampleCertificate['algorithm']; issuer: string }[] = [
    { name: 'JV Solara Peppol Service Provider', type: 'peppol-pki',     storedIn: 'CloudHSM',         daysToExpiry: 84,  alg: 'RSA-2048', issuer: 'OpenPeppol Root CA G3' },
    { name: 'mTLS · FTA EmaraTax production',     type: 'mtls-emaratax',  storedIn: 'CloudHSM',         daysToExpiry: 152, alg: 'RSA-2048', issuer: 'DigiCert Trust Services UAE' },
    { name: 'mTLS · TickStar API (production)',     type: 'mtls-tickstar',    storedIn: 'CloudHSM',         daysToExpiry: 311, alg: 'RSA-2048', issuer: 'TickStar Root CA' },
    { name: 'mTLS · TickStar API (sandbox)',         type: 'mtls-tickstar',   storedIn: 'Secrets Manager',  daysToExpiry: 28,  alg: 'RSA-2048', issuer: 'TickStar Root CA — Test' },
    { name: 'RFC 3161 Time-Stamping Authority',    type: 'rfc-3161-tsa',  storedIn: 'KMS-managed',      daysToExpiry: 540, alg: 'ECDSA-P256', issuer: 'GlobalSign R6 TSA' },
    { name: 'CDN edge — admin.solara.ae',          type: 'tls-cdn',       storedIn: 'KMS-managed',      daysToExpiry: 67,  alg: 'ECDSA-P384', issuer: 'Let\'s Encrypt R10' },
    { name: 'CDN edge — portal.solara.ae',         type: 'tls-cdn',       storedIn: 'KMS-managed',      daysToExpiry: 67,  alg: 'ECDSA-P384', issuer: 'Let\'s Encrypt R10' },
    { name: 'CDN edge — api.solara.ae',            type: 'tls-cdn',       storedIn: 'KMS-managed',      daysToExpiry: 11,  alg: 'ECDSA-P384', issuer: 'Let\'s Encrypt R10' },
  ];
  return items.map((it, idx) => {
    const notAfter = new Date(Date.now() + it.daysToExpiry * 86400_000);
    const notBefore = new Date(notAfter.getTime() - 365 * 86400_000);
    return {
      certId: `cert-${String(idx + 1).padStart(3, '0')}`,
      name: it.name,
      type: it.type,
      thumbprint: fakeThumbprint(),
      algorithm: it.alg,
      issuer: it.issuer,
      notBefore: notBefore.toISOString(),
      notAfter: notAfter.toISOString(),
      ocspStatus: it.daysToExpiry < 0 ? 'revoked' : 'good',
      storedIn: it.storedIn,
    };
  });
}

export function generateHsmStatus(): SampleHsm {
  return {
    clusterId: 'cluster-mecentral-1-prod-001',
    region: 'AWS me-central-1 (Dubai)',
    status: 'healthy',
    primaryHsm: 'hsm-1a-primary · az me-central-1a',
    secondaryHsm: 'hsm-1b-replica · az me-central-1b',
    signingsLast24h: 4287,
    cpuPct: 23,
  };
}

// --- Accreditation reports --------------------------------------

export interface AccreditationReport {
  reportId: string;
  title: string;
  period: string;
  generatedAt: string;
  generatedBy: string;
  sizeBytes: number;
  format: 'pdf' | 'csv' | 'xlsx';
  category: 'fta-audit' | 'iso-27001' | 'iso-22301' | 'peppol' | 'retention' | 'incident';
}

export function generateReports(count = 22, seed = 0xa1_b2_c3): AccreditationReport[] {
  const rng = createRng(seed);
  const samples: { title: string; cat: AccreditationReport['category']; format: AccreditationReport['format'] }[] = [
    { title: 'FTA quarterly audit export — Q1 2026',          cat: 'fta-audit',    format: 'pdf'  },
    { title: 'FTA monthly invoice register — March 2026',     cat: 'fta-audit',    format: 'csv'  },
    { title: 'ISO 27001 Annex A control matrix',              cat: 'iso-27001',    format: 'xlsx' },
    { title: 'ISO 22301 BCMS gap analysis',                   cat: 'iso-22301',    format: 'pdf'  },
    { title: 'Peppol Service Provider compliance — 2026',     cat: 'peppol',       format: 'pdf'  },
    { title: '7-year retention export — Tenant 0088:AETRN…',  cat: 'retention',    format: 'csv'  },
    { title: 'Incident postmortem · TickStar webhook DLQ spike', cat: 'incident',     format: 'pdf'  },
    { title: 'Penetration test report (annual)',               cat: 'iso-27001',    format: 'pdf'  },
    { title: 'DR drill — RTO 4h / RPO 1h evidence',           cat: 'iso-22301',    format: 'pdf'  },
    { title: 'PINT-AE conformance test summary',               cat: 'peppol',       format: 'pdf'  },
    { title: 'TDD submission rate — Q1 2026',                 cat: 'fta-audit',    format: 'xlsx' },
    { title: 'Vendor SLA compliance — TickStar',                cat: 'incident',     format: 'pdf'  },
  ];
  const out: AccreditationReport[] = [];
  for (let i = 0; i < count; i++) {
    const s = samples[i % samples.length]!;
    const ago = rng.int(1, 200);
    out.push({
      reportId: `rpt-${String(i + 1).padStart(3, '0')}`,
      title: s.title,
      period: rng.pick(['2026-Q1', '2026-03', '2026-02', '2026-01', '2025-Q4']),
      generatedAt: new Date(Date.now() - ago * 86400_000).toISOString(),
      generatedBy: rng.pick(['platform-admin@solara.ae', 'compliance@solara.ae', 'auto-job-cron']),
      sizeBytes: rng.int(80_000, 12_500_000),
      format: s.format,
      category: s.cat,
    });
  }
  out.sort((a, b) => (a.generatedAt < b.generatedAt ? 1 : -1));
  return out;
}

// --- Cross-tenant invoice rows (admin overview) ------------------

export interface AdminInvoiceRow {
  invoiceId: string;
  invoiceNumber: string;
  tenantId: string;
  tenantName: string;
  tenantNameAr: string;
  buyerName: string;
  totalGrossAmount: number;
  vatAmount: number;
  currency: 'AED';
  issueDate: string;
  mlsStatus: 'AP' | 'AB' | 'RE' | 'PENDING';
  direction: 'OUTBOUND' | 'INBOUND';
}

/**
 * Synthesise cross-tenant invoice rows for the admin's "all invoices" view.
 * The customer-portal invoices are tenant-scoped; here we cross many tenants.
 */
export function generateAdminInvoiceRows(
  count: number,
  tenantSamples: { tenantId: string; companyNameEn: string; companyNameAr: string }[],
  seed = 0xc0_55_aa,
): AdminInvoiceRow[] {
  const rng = createRng(seed);
  const buyers = [
    'Emaar Properties PJSC',
    'DP World FZE',
    'Etisalat by e&',
    'Aldar Properties',
    'ADNOC Distribution',
    'Mubadala Investment Co.',
    'Etihad Airways',
    'Jumeirah Group',
    'Dubai Holding',
    'Damac Properties',
  ];
  const rows: AdminInvoiceRow[] = [];
  for (let i = 0; i < count; i++) {
    const tenant = rng.pick(tenantSamples);
    const days = rng.int(0, 60);
    const at = new Date();
    at.setUTCDate(at.getUTCDate() - days);
    const r = rng.next();
    const status: AdminInvoiceRow['mlsStatus'] =
      r < 0.7 ? 'AP' : r < 0.85 ? 'AB' : r < 0.95 ? 'RE' : 'PENDING';
    const total = rng.int(800, 220_000);
    const vat = Math.round(total * 0.05);
    rows.push({
      invoiceId: `${'aaaaaaaa'}-${rng.int(1000, 9999).toString(16)}-4def-aaaa-${'cccccccccccc'}`,
      invoiceNumber: `INV-2026-${String(rng.int(1, 99)).padStart(2, '0')}-${String(rng.int(1, 9999)).padStart(4, '0')}`,
      tenantId: tenant.tenantId,
      tenantName: tenant.companyNameEn,
      tenantNameAr: tenant.companyNameAr,
      buyerName: rng.pick(buyers),
      totalGrossAmount: total,
      vatAmount: vat,
      currency: 'AED',
      issueDate: at.toISOString().slice(0, 10),
      mlsStatus: status,
      direction: rng.chance(0.85) ? 'OUTBOUND' : 'INBOUND',
    });
  }
  rows.sort((a, b) => (a.issueDate < b.issueDate ? 1 : -1));
  return rows;
}
